import { useEffect, useState } from 'react'

import { useAuth } from '../../../../../hooks/use-auth'
import type { UserDetails } from '../../../../../types/user'
import { EmailPhoneForm } from '../../shared/email-phone-form'
import { useMemberSearch } from './hooks/use-member-search'
import { useWorkspaceMembersAndInvitations } from './hooks/use-members-invitations'
import { useTeamMemberMutations } from './hooks/use-team-member-mutations'
import { useTeamMemberSelection } from './hooks/use-team-member-selection'
import { MemberSelectionList } from './member-selection-list'

interface TeamMemberSelectionProps {
  selectedUsers?: UserDetails[]
  shipmentID: string
  workspaceID: string
}

/**
 * This component is used to select team members for a shipment.
 * It allows the user to search for and select team members from the workspace.
 */
export const TeamMemberSelection = ({ selectedUsers = [], workspaceID, shipmentID }: TeamMemberSelectionProps) => {
  const { selectedWorkspace } = useAuth()

  const [openInvitePopup, setOpenInvitePopup] = useState(false)
  const { workspaceMembers, workspaceInvitations } = useWorkspaceMembersAndInvitations(
    workspaceID,
    selectedWorkspace?.workspace.id || ''
  )

  const {
    handleAddTeamMember,
    handleRemoveTeamMember,
    isLoading: mutationLoading
  } = useTeamMemberMutations(workspaceID, shipmentID, () => {
    setOpenInvitePopup(false)
  })

  const { userInput, setUserInput, loading, handleSubmit } = useTeamMemberSelection(
    workspaceID,
    selectedUsers,
    setOpenInvitePopup,
    handleAddTeamMember,
    workspaceMembers,
    workspaceInvitations
  )

  const searchResults = useMemberSearch({
    invitedUsers: workspaceInvitations,
    selectedUsers: selectedUsers.map((user) => ({ user })),
    suggestedUsers: workspaceMembers,
    userInput
  })

  const handleAddUser = (clickedUser: UserDetails) => {
    const { id, email, phone, displayName, role, workspaceID: userWorkspaceID } = clickedUser

    const isSelected = selectedUsers.some((selected) => selected.id === id)
    if (isSelected) return

    const emailOrPhone = email || phone || ''
    handleAddTeamMember({
      emailOrPhone,
      id,
      membership: { role },
      user: clickedUser,
      userWorkspaceID,
      workspace: { displayName: displayName || '', id: userWorkspaceID || '', workspacename: '' }
    })
  }

  const handleRemoveUser = (clickedUser: UserDetails) => {
    const { id } = clickedUser
    const isSelected = selectedUsers.some((selected) => selected.id === id)
    if (!isSelected) return

    handleRemoveTeamMember(id)
  }

  useEffect(() => {
    if (!openInvitePopup) setUserInput('')
  }, [openInvitePopup])

  if (openInvitePopup) {
    return <EmailPhoneForm onSubmit={handleSubmit} loading={loading} initialValue={userInput} />
  }

  return (
    <MemberSelectionList
      members={searchResults}
      onSearchInput={setUserInput}
      selectedMembers={selectedUsers}
      isLoadingMember={mutationLoading}
      onInviteClick={setOpenInvitePopup}
      onMemberSelect={handleAddUser}
      onMemberRemove={handleRemoveUser}
      membersWorkspace={workspaceMembers}
    />
  )
}
